import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import * as THREE from 'three';

interface SendAnimation3DProps {
  isVisible: boolean;
  postcardImageUrl: string;
  onComplete: () => void;
}

export function SendAnimation3D({ isVisible, postcardImageUrl, onComplete }: SendAnimation3DProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const globeRef = useRef<THREE.Mesh | null>(null);
  const postcardRef = useRef<THREE.Mesh | null>(null);
  const envelopeRef = useRef<THREE.Group | null>(null);


  // Initialize Three.js scene
  useEffect(() => {
    if (!isVisible || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!container) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 5);
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 10, 5);
    scene.add(directionalLight);

    // Create globe
    const globeGeometry = new THREE.SphereGeometry(2, 32, 32);
    const globeMaterial = new THREE.MeshPhongMaterial({
      color: 0x4a90e2,
      transparent: true,
      opacity: 0.8,
      shininess: 100
    });
    const globe = new THREE.Mesh(globeGeometry, globeMaterial);
    scene.add(globe);
    globeRef.current = globe;

    // Add globe texture (simple pattern)
    const globeTexture = createGlobeTexture();
    globeMaterial.map = globeTexture;

    // Create envelope
    const envelopeGroup = new THREE.Group();
    
    // Envelope body
    const envelopeGeometry = new THREE.BoxGeometry(1.5, 1, 0.1);
    const envelopeMaterial = new THREE.MeshPhongMaterial({ color: 0xffffff });
    const envelopeBody = new THREE.Mesh(envelopeGeometry, envelopeMaterial);
    envelopeGroup.add(envelopeBody);

    // Envelope flap
    const flapGeometry = new THREE.BoxGeometry(1.5, 0.8, 0.05);
    const flapMaterial = new THREE.MeshPhongMaterial({ color: 0xf0f0f0 });
    const flap = new THREE.Mesh(flapGeometry, flapMaterial);
    flap.position.set(0, 0.4, 0.05);
    envelopeGroup.add(flap);
    envelopeRef.current = envelopeGroup;
    scene.add(envelopeGroup);

    // Create postcard
    const postcardGeometry = new THREE.PlaneGeometry(1.2, 0.8);
    const postcardMaterial = new THREE.MeshBasicMaterial({
      map: createPostcardTexture(postcardImageUrl),
      side: THREE.DoubleSide
    });
    const postcard = new THREE.Mesh(postcardGeometry, postcardMaterial);
    postcard.position.set(0, 0, 0.1);
    postcardRef.current = postcard;
    envelopeGroup.add(postcard);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      // Rotate globe
      if (globeRef.current) {
        globeRef.current.rotation.y += 0.005;
      }
      
      renderer.render(scene, camera);
    };
    animate();

    // Cleanup
    return () => {
      renderer.dispose();
      scene.clear();
    };
  }, [isVisible, postcardImageUrl]);

  // Create globe texture
  const createGlobeTexture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // Draw ocean background
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#1e3a8a');
    gradient.addColorStop(0.5, '#3b82f6');
    gradient.addColorStop(1, '#1e40af');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw continents with more realistic shapes
    ctx.fillStyle = '#059669';
    
    // North America
    ctx.beginPath();
    ctx.moveTo(100, 80);
    ctx.lineTo(180, 60);
    ctx.lineTo(220, 100);
    ctx.lineTo(200, 150);
    ctx.lineTo(120, 180);
    ctx.lineTo(80, 140);
    ctx.closePath();
    ctx.fill();
    
    // South America
    ctx.beginPath();
    ctx.moveTo(180, 200);
    ctx.lineTo(200, 220);
    ctx.lineTo(220, 280);
    ctx.lineTo(200, 350);
    ctx.lineTo(180, 380);
    ctx.lineTo(160, 350);
    ctx.lineTo(140, 300);
    ctx.closePath();
    ctx.fill();
    
    // Europe/Asia
    ctx.beginPath();
    ctx.moveTo(300, 80);
    ctx.lineTo(450, 60);
    ctx.lineTo(500, 100);
    ctx.lineTo(480, 140);
    ctx.lineTo(400, 160);
    ctx.lineTo(320, 140);
    ctx.closePath();
    ctx.fill();
    
    // Africa
    ctx.beginPath();
    ctx.moveTo(320, 180);
    ctx.lineTo(340, 200);
    ctx.lineTo(360, 280);
    ctx.lineTo(340, 350);
    ctx.lineTo(320, 380);
    ctx.lineTo(300, 350);
    ctx.lineTo(280, 280);
    ctx.closePath();
    ctx.fill();
    
    // Australia
    ctx.beginPath();
    ctx.moveTo(600, 300);
    ctx.lineTo(650, 280);
    ctx.lineTo(680, 300);
    ctx.lineTo(670, 340);
    ctx.lineTo(640, 360);
    ctx.lineTo(610, 340);
    ctx.closePath();
    ctx.fill();

    // Add some cloud patterns
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.beginPath();
    ctx.arc(150, 120, 20, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(400, 200, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(550, 150, 25, 0, Math.PI * 2);
    ctx.fill();

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    return texture;
  };

  // Create postcard texture
  const createPostcardTexture = (imageUrl: string) => {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add border
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 4;
    ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);

    // Load and draw image if available
    if (imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        let drawWidth = canvas.width - 20;
        let drawHeight = canvas.height - 20;
        
        if (aspectRatio > 1) {
          drawHeight = drawWidth / aspectRatio;
        } else {
          drawWidth = drawHeight * aspectRatio;
        }
        
        const x = (canvas.width - drawWidth) / 2;
        const y = (canvas.height - drawHeight) / 2;
        
        ctx.drawImage(img, x, y, drawWidth, drawHeight);
      };
      img.src = imageUrl;
    }

    return new THREE.CanvasTexture(canvas);
  };

  // Run animation sequence
  useEffect(() => {
    if (!isVisible || !envelopeRef.current || !globeRef.current || !postcardRef.current) return;

    const tl = gsap.timeline({
      onComplete: () => {
        setTimeout(onComplete, 1000);
      }
    });

    // Step 1: Postcard scales in
    tl.set(postcardRef.current.scale, { x: 0, y: 0, z: 0 })
      .to(postcardRef.current.scale, {
        x: 1, y: 1, z: 1,
        duration: 0.8,
        ease: "back.out(1.7)"
      })
      .set({}, {}, "+=0.2");

    // Step 2: Postcard slides into envelope
    tl.to(postcardRef.current.position, {
      z: 0.05,
      duration: 0.6,
      ease: "power2.inOut"
    })
    .to(envelopeRef.current!.children[1].rotation, { // Flap
      x: -Math.PI / 2,
      duration: 0.4,
      ease: "power2.inOut"
    }, "-=0.3")
    .set({}, {}, "+=0.3");

    // Step 3: Envelope moves to globe
    tl.to(envelopeRef.current!.position, {
      x: 0,
      y: 0,
      z: 3,
      duration: 1.2,
      ease: "power2.inOut"
    })
    .to(envelopeRef.current!.rotation, {
      y: Math.PI * 2,
      duration: 1.2,
      ease: "power2.inOut"
    }, "-=1.2")
    .set({}, {}, "+=0.5");

    // Step 4: Envelope flies to destination on globe with curved path
    const destinationPoint = new THREE.Vector3(
      Math.random() * 4 - 2,
      Math.random() * 2 - 1,
      Math.random() * 2 - 1
    );
    destinationPoint.normalize().multiplyScalar(2.2);

    // Create curved flight path
    const startPos = envelopeRef.current!.position.clone();
    const midPoint = new THREE.Vector3(
      (startPos.x + destinationPoint.x) / 2,
      (startPos.y + destinationPoint.y) / 2 + 2,
      (startPos.z + destinationPoint.z) / 2
    );

    // Animate along curved path
    tl.to(envelopeRef.current!.position, {
      x: midPoint.x,
      y: midPoint.y,
      z: midPoint.z,
      duration: 1,
      ease: "power2.out"
    })
    .to(envelopeRef.current!.position, {
      x: destinationPoint.x,
      y: destinationPoint.y,
      z: destinationPoint.z,
      duration: 1,
      ease: "power2.in"
    })
    .to(envelopeRef.current!.scale, {
      x: 0.05,
      y: 0.05,
      z: 0.05,
      duration: 1.5,
      ease: "power2.inOut"
    }, "-=1")
    .set({}, {}, "+=0.5");

    // Step 5: Success effect with particles
    tl.to(globeRef.current!.material, {
      opacity: 1,
      duration: 0.5
    })
    .to(globeRef.current!.scale, {
      x: 1.3,
      y: 1.3,
      z: 1.3,
      duration: 0.6,
      ease: "back.out(1.7)"
    }, "-=0.5")
    .to(globeRef.current!.scale, {
      x: 1,
      y: 1,
      z: 1,
      duration: 0.4
    })
    .to(globeRef.current!.rotation, {
      y: globeRef.current!.rotation.y + Math.PI * 2,
      duration: 1,
      ease: "power2.out"
    }, "-=0.4");

  }, [isVisible, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
        >
          <div ref={containerRef} className="relative w-full h-full max-w-2xl max-h-2xl">
            <canvas
              ref={canvasRef}
              className="w-full h-full"
            />
            
                         {/* UI Overlay */}
             <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
               <motion.div
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ delay: 0.5 }}
                 className="text-white text-center"
               >
                 <div className="text-3xl font-bold mb-3 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                   Sending Postcard...
                 </div>
                 <div className="text-lg opacity-90 mb-4">Your message is traveling around the world</div>
                 
                 {/* Progress indicators */}
                 <div className="flex space-x-2 justify-center">
                   <motion.div
                     initial={{ scale: 0 }}
                     animate={{ scale: 1 }}
                     transition={{ delay: 1, duration: 0.3 }}
                     className="w-3 h-3 bg-blue-400 rounded-full"
                   />
                   <motion.div
                     initial={{ scale: 0 }}
                     animate={{ scale: 1 }}
                     transition={{ delay: 1.5, duration: 0.3 }}
                     className="w-3 h-3 bg-purple-400 rounded-full"
                   />
                   <motion.div
                     initial={{ scale: 0 }}
                     animate={{ scale: 1 }}
                     transition={{ delay: 2, duration: 0.3 }}
                     className="w-3 h-3 bg-green-400 rounded-full"
                   />
                 </div>
                 
                 <motion.div
                   initial={{ opacity: 0 }}
                   animate={{ opacity: 1 }}
                   transition={{ delay: 3 }}
                   className="text-sm opacity-75 mt-4"
                 >
                   ✈️ Flying to destination...
                 </motion.div>
               </motion.div>
             </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
